import { memo, useEffect, useRef } from 'react'

import useTable from '../states/useTable'
import useComponentLayout from '../states/useComponentLayout'

import CompareItemsTableRow from './CompareItemsTableRow'

import style from './CompareItemsTable.module.scss'
import addItemCellStyle from './cells/AddItemCell.module.scss'

export default memo(CompareItemsTable, () => true)
export function CompareItemsTable() {
  const table = useTable()

  const topRows = table.rows.slice(0, table.dataRowsStartIndex)
  const dataRows = table.rows.slice(table.dataRowsStartIndex)

  // set component layout measures
  const componentLayout = useComponentLayout()
  const tableRef = useRef<HTMLDivElement>(null)
  const dataRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (null != tableRef.current && null != dataRef.current) {
      const measureTimeout = setTimeout(() => {
        const attributeColFullWidth = table.attributeColFullWidth || 0
        const visualContentWidth = tableRef.current.offsetWidth
        const componentRoot: HTMLDivElement = tableRef.current.closest?.('[data-compare-items="root"]')
        const visualContentHeight = componentRoot?.offsetHeight || 0
        const dataVisualWidth = visualContentWidth - attributeColFullWidth
        const dataHeight = dataRef.current.offsetHeight
        const currentMeasures = componentLayout.measures || {}
        if (
          currentMeasures.visualContentWidth !== visualContentWidth ||
          currentMeasures.visualContentHeight !== visualContentHeight ||
          currentMeasures.dataVisualWidth !== dataVisualWidth ||
          currentMeasures.dataHeight !== dataHeight ||
          currentMeasures.attributeColFullWidth !== attributeColFullWidth
        ) {
          componentLayout.setMeasures({
            visualContentWidth,
            visualContentHeight,
            dataVisualWidth,
            dataHeight,
            attributeColFullWidth
          })
        }
      }, 100)
      return () => void clearTimeout(measureTimeout)
    }
    return void 0
  }, [componentLayout, table.attributeColFullWidth, table.rows.length])

  // auto show plus button
  const wrapperRef = useRef<HTMLDivElement>(null)
  const scrollToAddItemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (null != wrapperRef.current && null != scrollToAddItemRef.current && 'IntersectionObserver' in window) {
      const addItemCell = wrapperRef.current.querySelector(`.${addItemCellStyle.CompareItemsTableAddItem}`)
      if (null != addItemCell) {
        const observer = new IntersectionObserver(
          entries => {
            if (null != scrollToAddItemRef.current) {
              entries.forEach(entry => {
                scrollToAddItemRef.current.style.visibility = entry.isIntersecting ? '' : 'visible'
              })
            }
          },
          {
            root: wrapperRef.current,
            threshold: 0.5
          }
        )
        observer.observe(addItemCell)
        return () => void observer.disconnect()
      } else {
        return void 0
      }
    } else {
      return void 0
    }
  }, [table.canAddItems])

  return (
    <div className={style.CompareItemsTable__wrapper} ref={wrapperRef}>
      {table.canAddItems ? (
        <div
          className={style.CompareItemsTable__scroll_to_add_item}
          title={`add ${table.itemName}`}
          ref={scrollToAddItemRef}
          onClick={() => {
            const tableElement = tableRef.current
            if (null != tableElement) {
              tableElement.scrollTo({
                left: tableElement.scrollWidth,
                behavior: 'auto'
              })
            }
            const itemSelector = wrapperRef.current.querySelector<HTMLDivElement>(
              `.${addItemCellStyle.CompareItemsTableAddItem} .rfd-ui-inline-select`
            )
            itemSelector?.click?.()
          }}
        >
          +
        </div>
      ) : null}
      <div className={style.CompareItemsTable} ref={tableRef} data-compare-items="table">
        <div className={style.CompareItemsTable__content}>
          {topRows.map((row, rowIndex) => (
            <CompareItemsTableRow row={row} key={row.key || rowIndex} />
          ))}
          <div className={style.CompareItemsTable__data_rows} ref={dataRef}>
            {dataRows.map((row, rowIndex) => (
              <CompareItemsTableRow row={row} key={row.key || rowIndex} even={0 === rowIndex % 2} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
